import { useRef, useState, type CSSProperties, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { isValidNewsletterEmail } from "@majourneys/shared";
import { Icon } from "@majourneys/icons";
import { PEWTER_OUTLINE, pewterEdge, solid } from "../../../shared/ui/pewter-edge";
import { subscribeToNewsletter } from "../api/newsletter.api";
import { BEVEL_INSET_NAVY, BEVEL_NAVY_FACE } from "@majourneys/shared/ui";

const NAVY = "#110E39";
const MUTED = "#5a5d77";
const ERROR = "#b42318";
const PRIMARY_SHADOW = `0 10px 24px -12px rgba(17,14,57,0.8), ${BEVEL_INSET_NAVY}`;

/**
 * The newsletter field: the pewter band, 3px wide, around a white pill. Focus
 * darkens the pill's own hairline and adds a soft ring outside the band;
 * `mj-field-focus` animates the change. Inline because the value carries
 * commas, which Tailwind will not emit as an arbitrary shadow.
 */
function fieldStyle(focused: boolean, invalid: boolean): CSSProperties {
  const line = invalid ? "rgba(180,35,24,0.7)" : focused ? "rgba(17,14,57,0.6)" : "rgba(17,14,57,0.13)";
  return {
    ...pewterEdge(solid("#fff"), 999, {
      tone: "light",
      style: "pewter",
      width: 3,
      base: solid("rgba(255,255,255,1)"),
    }),
    boxShadow: [
      `0 0 0 1px ${PEWTER_OUTLINE}`,
      `inset 0 0 0 1px ${line}`,
      focused ? "0 0 0 4px rgba(17,14,57,0.08)" : "0 0 0 0 rgba(17,14,57,0)",
    ].join(", "),
  };
}

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

/** One ~300ms shake of the field (motion kit). Skipped under reduced motion. */
function shake(el: HTMLElement | null) {
  if (!el || prefersReducedMotion() || typeof el.animate !== "function") return;
  el.animate(
    [
      { transform: "translateX(0)" },
      { transform: "translateX(-6px)" },
      { transform: "translateX(5px)" },
      { transform: "translateX(-3px)" },
      { transform: "translateX(0)" },
    ],
    { duration: 300, easing: "cubic-bezier(0.23, 1, 0.32, 1)" },
  );
}

type Status = "idle" | "sending" | "sent" | "invalid" | "error";

/**
 * The footer's email signup. Enter or the button submits; a bad address
 * shakes the field and says so; a good one shows "Sending…" and then a
 * plain confirmation line in place of the subtitle.
 */
export function NewsletterForm() {
  const { t, i18n } = useTranslation();
  // Focus is state rather than :focus-within so the ring can be an inline
  // shadow. Both renders start unfocused, so the server and client agree.
  const [focused, setFocused] = useState(false);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const fieldRef = useRef<HTMLDivElement>(null);
  const honeypotRef = useRef<HTMLInputElement>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status === "sending") return;
    if (!isValidNewsletterEmail(email)) {
      setStatus("invalid");
      shake(fieldRef.current);
      return;
    }
    setStatus("sending");
    try {
      await subscribeToNewsletter({
        email: email.trim(),
        lang: i18n.language,
        company: honeypotRef.current?.value ?? "",
      });
      setEmail("");
      setStatus("sent");
    } catch {
      setStatus("error");
      shake(fieldRef.current);
    }
  }

  const sending = status === "sending";
  const sent = status === "sent";
  const message =
    status === "invalid"
      ? { text: t("footer.newsletter_invalid"), color: ERROR, role: "alert" as const }
      : status === "error"
        ? { text: t("footer.newsletter_error"), color: ERROR, role: "alert" as const }
        : sent
          ? { text: t("footer.newsletter_success"), color: NAVY, role: "status" as const }
          : null;

  return (
    <form noValidate onSubmit={onSubmit} className="flex flex-col gap-2.5">
      <div
        ref={fieldRef}
        className="mj-field-focus flex items-center gap-2 py-1.5 pe-1.5 ps-4"
        style={fieldStyle(focused, status === "invalid" || status === "error")}
      >
        <label htmlFor="footer-newsletter-email" className="sr-only">
          {t("common.newsletter_placeholder")}
        </label>
        {/* Placeholder is the meta grey, not the lighter one: #9a9bb5 fails contrast on white at this size. */}
        <input
          id="footer-newsletter-email"
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            // Typing again clears the last verdict, so a fixed typo is not
            // still marked wrong and a second signup can be sent.
            if (status !== "idle" && status !== "sending") setStatus("idle");
          }}
          aria-invalid={status === "invalid" || undefined}
          aria-describedby="footer-newsletter-message"
          placeholder={t("common.newsletter_placeholder")}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          /*
            An <input> defaults to `size=20`, an intrinsic width of
            twenty characters. Safari uses that as a flex item's base
            size and will not shrink below it, `min-width: 0` or not, so
            on an iPhone (where the 16px font floor makes those twenty
            characters wider still) this field plus Subscribe ran past
            the screen edge and the whole page scrolled sideways, on
            every page, because the footer is on every page. Chrome
            shrinks the field and shows nothing wrong, which is how it
            shipped. See `header-search.tsx` for the same fix.
          */
          size={1}
          className="h-8 min-w-0 flex-1 bg-transparent text-[14px] outline-none placeholder:text-[#5a5d77]"
          style={{ color: NAVY }}
        />
        {/* Honeypot: off-screen and out of the tab order, so only bots fill it. */}
        <input
          ref={honeypotRef}
          type="text"
          name="company"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          defaultValue=""
          className="sr-only"
        />
        {/*
          On a phone the button is a round arrow and its words move to the
          screen reader. With the words beside it, the field kept 114px at
          320 wide, so "Enter your email" read "Enter your em", and the
          Spanish placeholder was cut even at 390. The words come back from
          `sm` up, and the line under the field still says what happened.
        */}
        <button
          type="submit"
          disabled={sending}
          aria-live="polite"
          className="mj-press inline-flex h-9 w-9 shrink-0 items-center justify-center gap-1.5 rounded-full text-[13px] font-semibold text-white disabled:cursor-default focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#110E39] sm:h-auto sm:w-auto sm:px-4 sm:py-2"
          style={{ background: BEVEL_NAVY_FACE, boxShadow: PRIMARY_SHADOW }}
        >
          {sending ? (
            <>
              <Icon name="ri-loader-4-line" className="animate-spin text-[15px]" aria-hidden />
              <span className="max-sm:sr-only">{t("footer.newsletter_sending")}</span>
            </>
          ) : sent ? (
            <>
              <Icon name="ri-checkbox-circle-line" className="mj-pop-in text-[15px]" aria-hidden />
              <span className="max-sm:sr-only">{t("footer.newsletter_subscribed")}</span>
            </>
          ) : (
            <>
              <span aria-hidden className="contents sm:hidden">
                <Icon name={i18n.language === "ar" ? "ri-arrow-left-line" : "ri-arrow-right-line"} className="text-[17px]" aria-hidden />
              </span>
              <span className="max-sm:sr-only">{t("common.newsletter_subscribe")}</span>
            </>
          )}
        </button>
      </div>
      <p
        id="footer-newsletter-message"
        key={status}
        role={message?.role}
        className={`text-[12.5px] md:text-[13px] ${message ? "mj-fade-in font-semibold" : ""}`}
        style={{ color: message?.color ?? MUTED }}
      >
        {message?.text ?? t("footer.newsletter_subtitle")}
      </p>
    </form>
  );
}
